import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus } from 'lucide-react';
import WorkflowOrganizationService from '../../services/WorkflowOrganizationService';
import ParentWorkflowCard from './ParentWorkflowCard';
import TimelineSidebar from './TimelineSidebar';
import EnhancedSprintBlock from './EnhancedSprintBlock';
import DeadlineIntelligenceService from '../../services/DeadlineIntelligenceService';

/**
 * WorkflowTimelineView
 *
 * Main timeline component using hierarchical workflow structure with
 * progressive disclosure based on contextual states.
 *
 * Implements the UX from Timeline-V5-workflow-hierarchy-mockup.html
 */
const WorkflowTimelineView = ({ timelineItems = [], onTaskUpdate, onSubWorkflowComplete, studentId, currentUser }) => {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Organize timeline items into workflows
  const workflows = useMemo(() => {
    return WorkflowOrganizationService.organizeIntoWorkflows(timelineItems);
  }, [timelineItems]);

  // Calculate summary stats and recovery metrics
  const summaryStats = useMemo(() => {
    return WorkflowOrganizationService.getSummaryStats(workflows);
  }, [workflows]);

  const recoveryMetrics = useMemo(() => {
    return WorkflowOrganizationService.calculateRecoveryMetrics(workflows);
  }, [workflows]);

  // Extract unique colleges from timeline items for deadline intelligence
  const colleges = useMemo(() => {
    const collegeMap = new Map();
    timelineItems.forEach(item => {
      if (item.college && item.college.id) {
        // Extract deadline data from nested global_colleges object
        const gc = item.college.global_colleges;

        // Use application_round from college_journey_colleges (user's selection)
        // Use name from global_colleges for consistency
        const applicationRound = item.college.application_round;
        const collegeName = gc?.name || item.college.name;

        collegeMap.set(item.college.id, {
          id: item.college.id,
          name: collegeName,
          college_name: collegeName,
          application_round: applicationRound, // This is the round the student selected
          category: item.college.category, // Tier: reach/target/safety
          priority_order: item.college.priority_order, // Strategic ordering
          global_colleges: gc, // Include the full global_colleges object for deadline lookups
          deadlines: {
            ed: gc?.ed_deadline,
            ed2: gc?.ed2_deadline,
            ea: gc?.ea_deadline,
            rd: gc?.rd_deadline
          }
        });
      }
    });

    const extractedColleges = Array.from(collegeMap.values());
    console.log('[WorkflowTimelineView] Extracted colleges with deadlines:', extractedColleges);
    console.log('[WorkflowTimelineView] Sample college data:', {
      id: extractedColleges[0]?.id,
      name: extractedColleges[0]?.name,
      category: extractedColleges[0]?.category,
      priority_order: extractedColleges[0]?.priority_order,
      has_global_colleges: !!extractedColleges[0]?.global_colleges
    });
    return extractedColleges;
  }, [timelineItems]);

  // State for expanded workflows
  const [expandedWorkflows, setExpandedWorkflows] = useState(() => {
    // Auto-expand workflows in active states
    const initialExpanded = new Set();
    workflows.forEach(workflow => {
      if (workflow.isExpanded) {
        initialExpanded.add(workflow.workflowKey);
      }
    });
    return initialExpanded;
  });

  // State for expanded application round sections
  const [expandedRounds, setExpandedRounds] = useState(() => {
    const now = new Date();
    const septemberCutoff = new Date(2025, 8, 1); // September 1, 2025
    const shouldCollapseRD = now >= septemberCutoff;

    return {
      early_decision: true,
      early_action: true,
      regular_decision: !shouldCollapseRD // Collapsed after Sept 2025
    };
  });

  // State for pinned workflows (persisted to localStorage)
  const [pinnedWorkflows, setPinnedWorkflows] = useState(() => {
    try {
      const stored = localStorage.getItem(`timeline-pinned-${studentId}`);
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
      return new Set();
    }
  });

  const toggleWorkflow = (workflowKey) => {
    setExpandedWorkflows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(workflowKey)) {
        newSet.delete(workflowKey);
      } else {
        newSet.add(workflowKey);
      }
      return newSet;
    });
  };

  const toggleRound = (roundKey) => {
    setExpandedRounds(prev => ({
      ...prev,
      [roundKey]: !prev[roundKey]
    }));
  };

  const togglePin = (workflowKey) => {
    setPinnedWorkflows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(workflowKey)) {
        // Unpin
        newSet.delete(workflowKey);
      } else {
        // Pin - but only allow ONE pinned college workflow
        // Clear any existing college workflow pins first
        const existingCollegePins = [...newSet].filter(key =>
          key.startsWith('complete_') && key.endsWith('_app')
        );
        existingCollegePins.forEach(key => newSet.delete(key));

        // Add the new pin
        newSet.add(workflowKey);
      }

      // Persist to localStorage
      try {
        localStorage.setItem(`timeline-pinned-${studentId}`, JSON.stringify([...newSet]));
      } catch (error) {
        console.error('Failed to persist pinned workflows:', error);
      }

      return newSet;
    });
  };

  // Group and organize workflows
  const { collegeWorkflows, activeNonCollegeWorkflows, deferredWorkflows } = useMemo(() => {
    const college = [];
    const activeNonCollege = [];
    const deferred = [];

    const now = new Date();
    const septemberCutoff = new Date(2025, 8, 1); // September 1, 2025
    const shouldDeferScholarships = now >= septemberCutoff;

    workflows.forEach(workflow => {
      const isCollegeWorkflow = workflow.workflowKey?.startsWith('complete_') &&
                                 workflow.workflowKey?.endsWith('_app');

      if (isCollegeWorkflow) {
        college.push(workflow);
      } else if (workflow.workflowKey === 'prepare_for_enrollment') {
        // Always goes to bottom
        deferred.push(workflow);
      } else if (workflow.workflowKey === 'research_scholarships' && shouldDeferScholarships) {
        // Move to bottom after Sept 1
        deferred.push(workflow);
      } else {
        activeNonCollege.push(workflow);
      }
    });

    // Sort groups: pinned first, then by order
    const sortByPin = (a, b) => {
      const aIsPinned = pinnedWorkflows.has(a.workflowKey);
      const bIsPinned = pinnedWorkflows.has(b.workflowKey);
      if (aIsPinned && !bIsPinned) return -1;
      if (!aIsPinned && bIsPinned) return 1;
      return (a.order || 0) - (b.order || 0);
    };

    college.sort(sortByPin);
    activeNonCollege.sort(sortByPin);
    deferred.sort(sortByPin);

    return {
      collegeWorkflows: college,
      activeNonCollegeWorkflows: activeNonCollege,
      deferredWorkflows: deferred
    };
  }, [workflows, pinnedWorkflows]);

  // Extract pinned college workflow (only one allowed) and organize the rest
  const { pinnedCollegeWorkflow, edSchool, eaSchools, rdSchools } = useMemo(() => {
    let pinned = null;
    let ed = null;
    const ea = [];
    const rd = [];

    collegeWorkflows.forEach(workflow => {
      // Check if this workflow is pinned
      if (pinnedWorkflows.has(workflow.workflowKey)) {
        pinned = workflow;
        return; // Skip adding to ED/EA/RD if pinned
      }

      // Get application_round from the first timeline item in this workflow
      const workflowItems = timelineItems.filter(item => item.workflow_key === workflow.workflowKey);
      const applicationRound = workflowItems[0]?.college?.application_round || 'regular_decision';

      // Normalize the round key
      if (applicationRound === 'early_decision' || applicationRound === 'early_decision_1' || applicationRound === 'early_decision_2') {
        ed = workflow; // There's only ever one ED school
      } else if (applicationRound === 'early_action') {
        ea.push(workflow);
      } else {
        rd.push(workflow);
      }
    });

    return { pinnedCollegeWorkflow: pinned, edSchool: ed, eaSchools: ea, rdSchools: rd };
  }, [collegeWorkflows, timelineItems, pinnedWorkflows]);

  return (
    <div className="workflow-timeline-container" style={{
      maxWidth: '1600px',
      margin: '0 auto',
      padding: 'clamp(1rem, 5vw, 2rem)',
      fontFamily: "'Reddit Sans', system-ui, -apple-system, sans-serif"
    }}>
      {/* Mobile Sidebar Toggle Button */}
      <button
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        style={{
          display: 'none',
          position: 'fixed',
          bottom: '1rem',
          right: '1rem',
          width: '56px',
          height: '56px',
          borderRadius: '50%',
          background: '#0f172a',
          border: '2px solid #14b8a6',
          color: 'white',
          fontSize: '1.5rem',
          cursor: 'pointer',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
          zIndex: 100,
          alignItems: 'center',
          justifyContent: 'center'
        }}
        className="mobile-sidebar-toggle"
      >
        {isSidebarOpen ? '✕' : '☰'}
      </button>

      <style>{`
        @media (max-width: 1024px) {
          .mobile-sidebar-toggle {
            display: flex !important;
          }
        }
      `}</style>

      {/* Two-column layout: Main content + Sidebar */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: '2rem',
        alignItems: 'start'
      }}
      className="timeline-grid">
        <style>{`
          @media (min-width: 1024px) {
            .timeline-grid {
              grid-template-columns: 1fr 320px !important;
            }
          }
        `}</style>

        {/* Main Content Column */}
        <div>
          {/* Page Header */}
          <div className="timeline-page-header" style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '2rem',
            flexWrap: 'wrap',
            gap: '1rem'
          }}>
            {/* Left side: Back button and title */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              minWidth: 0,
              flex: '1 1 auto'
            }}>
              <button
                onClick={() => navigate('/college')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '40px',
                  height: '40px',
                  borderRadius: '8px',
                  background: 'white',
                  border: '2px solid #0f172a',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  flexShrink: 0
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f5f5f4';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'white';
                }}
                aria-label="Back to College Journey"
              >
                <ArrowLeft size={20} style={{ color: '#0f172a' }} />
              </button>

              <div style={{ minWidth: 0, flex: '1 1 auto' }}>
                <h1 style={{
                  fontSize: 'clamp(1.25rem, 3vw, 1.75rem)',
                  fontWeight: 700,
                  color: '#0f172a',
                  margin: 0,
                  lineHeight: 1.2
                }}>
                  Timeline
                </h1>
                <p style={{
                  fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
                  color: '#64748b',
                  margin: '0.25rem 0 0 0'
                }}>
                  Track your college application tasks
                </p>
              </div>
            </div>

            {/* Right side: Add Task button */}
            <button
              onClick={() => {
                // TODO: Open add task modal/drawer
                console.log('Add task clicked');
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1.5rem',
                background: '#4f46e5',
                color: 'white',
                border: '2px solid #0f172a',
                borderRadius: '8px',
                fontSize: '0.9375rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s',
                whiteSpace: 'nowrap',
                flexShrink: 0
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#4338ca';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#4f46e5';
              }}
            >
              <Plus size={18} />
              <span>Add Task</span>
            </button>
          </div>

          <style>{`
            @media (max-width: 640px) {
              .timeline-page-header {
                gap: 0.75rem !important;
              }
              .timeline-page-header button[aria-label="Back to College Journey"] {
                width: 36px !important;
                height: 36px !important;
              }
              .timeline-page-header button:last-child {
                width: 100%;
                justify-content: center;
              }
            }
          `}</style>

      {/* Enhanced Sprint Block - improved styling with deadline_reminder filtering
      <EnhancedSprintBlock
        timelineItems={timelineItems}
        colleges={colleges}
      />
*/}
      {/* COMMENTED OUT FOR NOW THROUGH LINE 338 Progress Summary 
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        <div style={{
          background: 'white',
          border: '2px solid #e7e5e4',
          borderRadius: '12px',
          padding: '1.5rem'
        }}>
          <h3 style={{
            fontSize: '0.875rem',
            fontWeight: 600,
            color: '#64748b',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: '1rem'
          }}>
            Active Workflows
          </h3>
          <div style={{ fontSize: '2.5rem', fontWeight: 700, color: '#0f172a', lineHeight: 1, marginBottom: '0.5rem' }}>
            {summaryStats.activeWorkflows}
          </div>
          <div style={{ color: '#64748b', fontSize: '0.875rem' }}>
            Major applications in progress
          </div>
        </div>

        {summaryStats.nextUnlockingStep && (
          <div style={{
            background: 'white',
            border: '2px solid #e7e5e4',
            borderRadius: '12px',
            padding: '1.5rem'
          }}>
            <h3 style={{
              fontSize: '0.875rem',
              fontWeight: 600,
              color: '#64748b',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: '1rem'
            }}>
              Next Up
            </h3>
            <div style={{ fontSize: '2.5rem', fontWeight: 700, color: '#0f172a', lineHeight: 1, marginBottom: '0.5rem' }}>
              1
            </div>
            <div style={{ color: '#64748b', fontSize: '0.875rem' }}>
              Step unlocks {summaryStats.nextUnlockingStep.unlocks}
            </div>
          </div>
        )}

        <div style={{
          background: 'white',
          border: '2px solid #e7e5e4',
          borderRadius: '12px',
          padding: '1.5rem'
        }}>
          <h3 style={{
            fontSize: '0.875rem',
            fontWeight: 600,
            color: '#64748b',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: '1rem'
          }}>
            Overall Progress
          </h3>
          <div style={{ fontSize: '2.5rem', fontWeight: 700, color: '#0f172a', lineHeight: 1, marginBottom: '0.5rem' }}>
            {summaryStats.completionPercentage}%
          </div>
          <div style={{ color: '#64748b', fontSize: '0.875rem' }}>
            {summaryStats.finishedSteps} of {summaryStats.totalSteps} steps finished
            {summaryStats.skippedSteps > 0 && (
              <span style={{ color: '#94a3b8', marginLeft: '0.5rem' }}>
                ({summaryStats.completedSteps} completed, {summaryStats.skippedSteps} skipped)
              </span>
            )}
          </div>
        </div>
      </div>
      */}

      {/* COMMENTED OUT - "Your Next Action" section (moved to /college/timeline-dev)
      {workflows.some(w => w.nextAction) && (
        <div style={{
          background: 'linear-gradient(135deg, #26a69a 0%, #1a6b65 100%)',
          border: '2px solid #0f172a',
          borderRadius: '12px',
          padding: '2rem',
          marginBottom: '2rem',
          color: 'white'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 600, margin: 0 }}>
              Your Next Action
            </h2>
          </div>
          <p style={{ opacity: 0.95, marginBottom: '1.5rem' }}>
            This is the single most important thing you can do right now
          </p>

          {(() => {
            const workflowWithNext = workflows.find(w => w.nextAction);
            if (!workflowWithNext) return null;
            const nextAction = workflowWithNext.nextAction;

            return (
              <div style={{
                background: 'white',
                border: '2px solid #0f172a',
                borderRadius: '10px',
                padding: '1.5rem',
                color: '#0f172a'
              }}>
                <div style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                  {nextAction.title}
                </div>
                <div style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '1rem' }}>
                  {nextAction.context}
                </div>
                {nextAction.step?.description && (
                  <p style={{ color: '#64748b', fontSize: '0.875rem', margin: 0 }}>
                    {nextAction.step.description}
                  </p>
                )}
              </div>
            );
          })()}
        </div>
      )}
      */}

      {/* Workflows Section */}
      <div style={{
        background: 'white',
        border: '2px solid #0f172a',
        borderRadius: '12px',
        padding: '2rem'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '1.5rem'
        }}>
          <div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: '#0f172a', margin: 0 }}>
              Your Workflows
            </h2>
            <p style={{ color: '#64748b', fontSize: '0.9375rem', marginTop: '0.5rem' }}>
              Click any workflow to see its components and steps
            </p>
          </div>
        </div>

        {/* Workflow Cards */}
        <div>
          {/* Pinned College Workflow - Appears at VERY TOP, above everything */}
          {pinnedCollegeWorkflow && (
            <div style={{ marginBottom: '2rem' }}>
              {/* Pinned Header */}
              <div style={{
                background: 'linear-gradient(135deg, #26a69a 0%, #1a6b65 100%)',
                border: '3px solid #0f172a',
                borderRadius: '12px',
                padding: '1rem 1.5rem',
                
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div style={{
                    background: 'white',
                    color: '#26a69a',
                    padding: '0.25rem',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M16 12V4H17V2H7V4H8V12L6 14V16H11.2V22H12.8V16H18V14L16 12Z" />
                    </svg>
                  </div>
                  <div style={{ flex: 1 }}>
                    
                    <p style={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.95)', margin: '0 0 0 0', fontWeight: 500 }}>
                      Your top priority workflow
                    </p>
                  </div>
                </div>
              </div>

              {/* Pinned School Card */}
              <ParentWorkflowCard
                key={pinnedCollegeWorkflow.workflowKey}
                workflow={pinnedCollegeWorkflow}
                workflowNumber={null}
                isExpanded={expandedWorkflows.has(pinnedCollegeWorkflow.workflowKey)}
                onToggle={() => toggleWorkflow(pinnedCollegeWorkflow.workflowKey)}
                onTaskUpdate={onTaskUpdate}
                onSubWorkflowComplete={onSubWorkflowComplete}
                navigate={navigate}
                isPinned={true}
                onTogglePin={togglePin}
              />
            </div>
          )}

          {/* Active non-college workflows (excludes deferred ones) */}
          {activeNonCollegeWorkflows.map((workflow, index) => (
            <ParentWorkflowCard
              key={workflow.workflowKey}
              workflow={workflow}
              workflowNumber={index + 1}
              isExpanded={expandedWorkflows.has(workflow.workflowKey)}
              onToggle={() => toggleWorkflow(workflow.workflowKey)}
              onTaskUpdate={onTaskUpdate}
              onSubWorkflowComplete={onSubWorkflowComplete}
              navigate={navigate}
              isPinned={pinnedWorkflows.has(workflow.workflowKey)}
              onTogglePin={togglePin}
            />
          ))}

          {/* Early Decision School - Special prominent display (only ever one) */}
          {edSchool && (
            <div style={{ marginTop: activeNonCollegeWorkflows.length > 0 ? '2rem' : '0', marginBottom: '2rem' }}>
              {/* Special ED Header */}
              <div style={{
                background: '#26a69a',
                border: '1px solid #0f172a',
                borderRadius: '12px',
                padding: '1rem 1.5rem',
                marginBottom: '-1.25rem',
                paddingBottom: '2rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  
                  <div style={{ flex: 1 }}>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'white', margin: 0, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Early Decision Application
                    </h3>
                    <p style={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.95)', margin: '0.25rem 0 0 0', fontWeight: 500 }}>
                      Binding commitment • Highest priority
                    </p>
                  </div>
                </div>
              </div>

              {/* ED School Card */}
              <ParentWorkflowCard
                key={edSchool.workflowKey}
                workflow={edSchool}
                workflowNumber={null}
                isExpanded={expandedWorkflows.has(edSchool.workflowKey)}
                onToggle={() => toggleWorkflow(edSchool.workflowKey)}
                onTaskUpdate={onTaskUpdate}
                onSubWorkflowComplete={onSubWorkflowComplete}
                navigate={navigate}
                isPinned={pinnedWorkflows.has(edSchool.workflowKey)}
                onTogglePin={togglePin}
              />
            </div>
          )}

          {/* Early Action Section */}
          {eaSchools.length > 0 && (
            <div style={{ marginTop: '2rem' }}>
              <button
                onClick={() => toggleRound('early_action')}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '1rem 1.5rem',
                  background: '#26a69a',
                  border: '1px solid #0f172a',
                  borderRadius: '12px',
                  marginBottom: '-1.25rem',
                  paddingBottom: '2rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  
                  <div style={{ textAlign: 'left' }}>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: 600, color: 'white', margin: 0 }}>
                      Early Action Applications
                    </h3>
                    <p style={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.9)', margin: '0.25rem 0 0 0' }}>
                      {eaSchools.length} {eaSchools.length === 1 ? 'school' : 'schools'} • Non-binding
                    </p>
                  </div>
                </div>
                <span style={{ fontSize: '1.5rem', color: 'white', transform: expandedRounds.early_action ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                  ▼
                </span>
              </button>

              {expandedRounds.early_action && eaSchools.map((workflow) => (
                <ParentWorkflowCard
                  key={workflow.workflowKey}
                  workflow={workflow}
                  workflowNumber={null}
                  isExpanded={expandedWorkflows.has(workflow.workflowKey)}
                  onToggle={() => toggleWorkflow(workflow.workflowKey)}
                  onTaskUpdate={onTaskUpdate}
                  onSubWorkflowComplete={onSubWorkflowComplete}
                  navigate={navigate}
                  isPinned={pinnedWorkflows.has(workflow.workflowKey)}
                  onTogglePin={togglePin}
                />
              ))}
            </div>
          )}

          {/* Regular Decision Section */}
          {rdSchools.length > 0 && (
            <div style={{ marginTop: '2rem' }}>
              <button
                onClick={() => toggleRound('regular_decision')}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '1rem 1.5rem',
                  background: expandedRounds.regular_decision ? 'linear-gradient(135deg, #64748b 0%, #475569 100%)' : '#f1f5f9',
                  border: '2px solid #0f172a',
                  borderRadius: '12px',
                  marginBottom: '1rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span style={{ fontSize: '1.5rem' }}>📅</span>
                  <div style={{ textAlign: 'left' }}>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: 600, color: expandedRounds.regular_decision ? 'white' : '#0f172a', margin: 0 }}>
                      Regular Decision Applications
                    </h3>
                    <p style={{ fontSize: '0.875rem', color: expandedRounds.regular_decision ? 'rgba(255, 255, 255, 0.9)' : '#64748b', margin: '0.25rem 0 0 0' }}>
                      {rdSchools.length} {rdSchools.length === 1 ? 'school' : 'schools'} • Standard timeline
                    </p>
                  </div>
                </div>
                <span style={{ fontSize: '1.5rem', color: expandedRounds.regular_decision ? 'white' : '#64748b', transform: expandedRounds.regular_decision ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                  ▼
                </span>
              </button>

              {expandedRounds.regular_decision && rdSchools.map((workflow) => (
                <ParentWorkflowCard
                  key={workflow.workflowKey}
                  workflow={workflow}
                  workflowNumber={null}
                  isExpanded={expandedWorkflows.has(workflow.workflowKey)}
                  onToggle={() => toggleWorkflow(workflow.workflowKey)}
                  onTaskUpdate={onTaskUpdate}
                  onSubWorkflowComplete={onSubWorkflowComplete}
                  navigate={navigate}
                  isPinned={pinnedWorkflows.has(workflow.workflowKey)}
                  onTogglePin={togglePin}
                />
              ))}
            </div>
          )}

          {/* Deferred Workflows - Always at bottom (Scholarships after Sept 1, Enrollment always) */}
          {deferredWorkflows.length > 0 && (
            <div style={{ marginTop: '2rem' }}>
              {deferredWorkflows.map((workflow) => (
                <ParentWorkflowCard
                  key={workflow.workflowKey}
                  workflow={workflow}
                  workflowNumber={null}
                  isExpanded={expandedWorkflows.has(workflow.workflowKey)}
                  onToggle={() => toggleWorkflow(workflow.workflowKey)}
                  onTaskUpdate={onTaskUpdate}
                  onSubWorkflowComplete={onSubWorkflowComplete}
                  navigate={navigate}
                  isPinned={pinnedWorkflows.has(workflow.workflowKey)}
                  onTogglePin={togglePin}
                />
              ))}
            </div>
          )}
        </div>

        {workflows.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '3rem',
            color: '#64748b'
          }}>
            <p style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>
              No workflows yet
            </p>
            <p style={{ fontSize: '0.875rem' }}>
              Complete your profile and add colleges to get started
            </p>
          </div>
        )}
      </div>
        </div>
        {/* End Main Content Column */}

        {/* Sidebar Column - Desktop */}
        <div className="sidebar-desktop">
          <TimelineSidebar
            timelineItems={timelineItems}
            workflows={workflows}
            studentId={studentId}
            currentUser={currentUser}
          />
        </div>
      </div>
      {/* End Two-column layout */}

      {/* Mobile Sidebar Drawer */}
      <div
        className="sidebar-mobile-drawer"
        style={{
          position: 'fixed',
          top: 0,
          right: isSidebarOpen ? 0 : '-100%',
          width: '85%',
          maxWidth: '400px',
          height: '100vh',
          background: '#fafaf9',
          boxShadow: isSidebarOpen ? '-4px 0 12px rgba(0, 0, 0, 0.2)' : 'none',
          transition: 'right 0.3s ease',
          zIndex: 101,
          overflowY: 'auto',
          padding: '1rem'
        }}
      >
        <TimelineSidebar
          timelineItems={timelineItems}
          workflows={workflows}
          studentId={studentId}
          currentUser={currentUser}
        />
      </div>

      {/* Mobile Sidebar Overlay */}
      {isSidebarOpen && (
        <div
          onClick={() => setIsSidebarOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 100
          }}
          className="sidebar-mobile-overlay"
        />
      )}

      <style>{`
        .sidebar-desktop {
          display: none;
        }

        @media (min-width: 1024px) {
          .sidebar-desktop {
            display: block;
          }
          .sidebar-mobile-drawer,
          .sidebar-mobile-overlay {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default WorkflowTimelineView;
